/**
 * Content Type Business Rules
 *
 * Centralized definitions for all content-type-specific behaviour on the Get Quote flow.
 * Controls: display messages, insurance eligibility, and carrier filtering.
 *
 * This is the TypeScript equivalent of content-type-rules.js used in the other
 * FastCourier repos (fastcourier-cp, fastcourier-admin) - kept in sync with them.
 */

import { ContentType } from './types';

export type CarrierFilter = {
  mode: 'include' | 'exclude';
  carriers: string[];
};

export type ContentTypeRule = {
  label: string;
  messages: string[];
  insuranceAllowed: boolean;
  carrierFilter: CarrierFilter | null;
};

export const CONTENT_TYPE_RULES: Record<string, ContentTypeRule> = {
  'alcohol': {
    label: 'Alcohol',
    messages: [
      'Alcohol shipments have carrier-specific restrictions.',
      'Some couriers do not transport alcohol.',
      'Additional packaging requirements may apply.',
    ],
    insuranceAllowed: true,
    carrierFilter: {
      mode: 'exclude',
      carriers: ['TSS Sensitive Freight'],
    },
  },
  'glass/fragile goods': {
    label: 'Glass / Fragile Goods',
    messages: [
      'Fragile items must be securely packaged.',
      'Additional carrier handling charges may apply.',
      'Damage resulting from inadequate packaging may not be covered.',
    ],
    insuranceAllowed: true,
    carrierFilter: null,
  },
  'other liquids': {
    label: 'Other Liquids',
    messages: [
      'Liquid shipments must be securely sealed to prevent leakage.',
      'Carrier restrictions may apply.',
    ],
    insuranceAllowed: true,
    carrierFilter: {
      mode: 'exclude',
      carriers: ['TSS Sensitive Freight'],
    },
  },
  'food': {
    label: 'Food',
    messages: [
      'Food items must comply with courier transport requirements.',
      'Perishable goods may have limited carrier availability.',
    ],
    insuranceAllowed: true,
    carrierFilter: null,
  },
  'plants': {
    label: 'Plants',
    messages: [
      "Live plants are transported at the sender's risk.",
      'Insurance is not available for plant shipments.',
    ],
    insuranceAllowed: false,
    carrierFilter: null,
  },
  'vehicle parts/wheels': {
    label: 'Vehicle Parts / Wheels',
    messages: [
      'Vehicle parts should be properly packaged.',
      'Sharp edges and exposed metal should be protected.',
    ],
    insuranceAllowed: true,
    carrierFilter: null,
  },
  'artwork': {
    label: 'Artwork',
    messages: [
      'Original artwork and similar valuable items require specialised packaging.',
      'Insurance is not available for Artwork shipments.',
    ],
    insuranceAllowed: false,
    carrierFilter: null,
  },
  'household contents/personal effects': {
    label: 'Household Contents / Personal Effects',
    messages: [
      'Used household and personal items are transported subject to courier acceptance.',
      'Insurance is not available for these items.',
    ],
    insuranceAllowed: false,
    carrierFilter: null,
  },
  'sensitive freight': {
    label: 'Sensitive Freight',
    messages: [
      'This includes sensitive items like Printers, IT Power and Tech Infrastructure, Gaming Machines, Medical & Healthcare Equipment, as well as any high-value bulky items.',
      'Only approved carriers support this content type.',
    ],
    insuranceAllowed: true,
    carrierFilter: {
      mode: 'include',
      carriers: ['TSS Sensitive Freight', 'TNT', 'Northline'],
    },
  },
  'other': {
    label: 'General/Others',
    messages: [
      'Select this option only if none of the listed content types are applicable.',
      'Courier acceptance may vary depending on shipment contents.',
    ],
    insuranceAllowed: true,
    carrierFilter: null,
  },
};

/**
 * Safe accessor for content type rules.
 * @param value - The content type value from the select (case-insensitive).
 * @returns The rule object, or null if not found.
 */
export function getContentTypeRule(value: ContentType | string | undefined | null): ContentTypeRule | null {
  if (!value) return null;
  const key = value.toLowerCase().trim();
  return CONTENT_TYPE_RULES[key] || null;
}

/**
 * Determine if insurance should be disabled based on all package content types.
 * Returns true if ANY package has a non-insurable content type.
 */
export function hasNonInsurableContent(items: Array<{ contents?: string }>): boolean {
  return items.some((item) => {
    const rule = getContentTypeRule(item.contents);
    return rule ? rule.insuranceAllowed === false : false;
  });
}

/**
 * Get the combined carrier filter based on all selected content types.
 * Rules:
 *   - If ANY item has mode='include', the final filter is the intersection of all include lists.
 *   - All 'exclude' carriers across items are unioned into a single exclusion list.
 *   - 'include' takes precedence over 'exclude' (if include is present, only those carriers show).
 */
export function getCombinedCarrierFilter(items: Array<{ contents?: string }>): CarrierFilter | null {
  const includeFilters: string[][] = [];
  const excludeCarriers: string[] = [];

  items.forEach((item) => {
    const rule = getContentTypeRule(item.contents);
    if (rule && rule.carrierFilter) {
      if (rule.carrierFilter.mode === 'include') {
        includeFilters.push(rule.carrierFilter.carriers);
      } else if (rule.carrierFilter.mode === 'exclude') {
        rule.carrierFilter.carriers.forEach((carrier) => {
          if (!excludeCarriers.includes(carrier)) {
            excludeCarriers.push(carrier);
          }
        });
      }
    }
  });

  if (includeFilters.length > 0) {
    const intersection = includeFilters.reduce((acc, list) => acc.filter((c) => list.includes(c)));
    return { mode: 'include', carriers: intersection };
  }

  if (excludeCarriers.length > 0) {
    return { mode: 'exclude', carriers: excludeCarriers };
  }

  return null;
}

/**
 * Build the description text shown in a hover-preview panel/tooltip for a given
 * content type value. Returns an empty string for unknown values.
 */
export function getContentTypeDescription(value: ContentType | string | undefined | null): string {
  const rule = getContentTypeRule(value);
  if (!rule) return '';
  return rule.messages.join(' ');
}
